/**
 * Returns all focusable elements within `container` in DOM order.
 * Skips elements that are hidden, disabled, aria-hidden, or have tabIndex < 0.
 */
export function getFocusableCells(container: HTMLElement): HTMLElement[] {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
    acceptNode(node) {
      const el = node as HTMLElement;
      if (el.getAttribute('aria-hidden') === 'true') {
        return NodeFilter.FILTER_REJECT;
      }
      if (el.hidden || (el as HTMLInputElement).disabled) {
        return NodeFilter.FILTER_SKIP;
      }
      if (el.tabIndex >= 0) {
        return NodeFilter.FILTER_ACCEPT;
      }
      return NodeFilter.FILTER_SKIP;
    },
  });
  const cells: HTMLElement[] = [];
  let node = walker.nextNode();
  while (node) {
    cells.push(node as HTMLElement);
    node = walker.nextNode();
  }
  return cells;
}

/**
 * Cmd/Ctrl+Arrow: jump to the very start (left) or end (right) of the whole
 * query — the first/last focusable cell in `root` — with the caret placed at the
 * corresponding edge of the landing input. Returns true when focus moved.
 */
export function focusQueryEdge(root: HTMLElement, direction: 'left' | 'right'): boolean {
  const cells = getFocusableCells(root);
  if (!cells.length) {
    return false;
  }
  const target = direction === 'left' ? cells[0] : cells[cells.length - 1];
  target.focus();
  if (target instanceof HTMLInputElement) {
    const pos = direction === 'left' ? 0 : target.value.length;
    target.setSelectionRange(pos, pos);
  }
  return true;
}

/**
 * Alt+Arrow: hop to the previous/next free-text gap — the empty insertion points
 * between lozenges (each a `[data-freetext-token]` input) — resolved by DOM
 * position relative to `fromEl`. Lozenges sit between the gaps, so this skips
 * over them. Returns true when focus moved.
 */
export function focusAdjacentGap(root: HTMLElement, fromEl: HTMLElement | null, direction: 'left' | 'right'): boolean {
  if (!fromEl) {
    return false;
  }
  const gaps = Array.from(root.querySelectorAll<HTMLElement>('[data-freetext-token]')).filter((g) => g !== fromEl);
  const wanted =
    direction === 'left'
      ? Node.DOCUMENT_POSITION_FOLLOWING // fromEl follows gap → gap precedes fromEl
      : Node.DOCUMENT_POSITION_PRECEDING; // fromEl precedes gap → gap follows fromEl
  // eslint-disable-next-line no-bitwise
  const candidates = gaps.filter((g) => g.compareDocumentPosition(fromEl) & wanted);
  const target = direction === 'left' ? candidates[candidates.length - 1] : candidates[0];
  if (!target) {
    return false;
  }
  target.focus();
  if (target instanceof HTMLInputElement) {
    const pos = direction === 'left' ? target.value.length : 0;
    target.setSelectionRange(pos, pos);
  }
  return true;
}
